/**
 * Structural edits on table cells: keeping borders and column indexes correct
 * after a row/column is added or removed, and applying per-cell alignment.
 *
 * These live outside the toolbar component because they are pure ProseMirror
 * transactions — the toolbar only decides when to run them.
 */

import type { Node as PMNode } from "@tiptap/pm/model";
import type { Editor } from "@tiptap/react";
import { hashCode } from "../hooks/helpers/tableStyleHelpers";
import {
	cellBorderWidthValue,
	DEFAULT_BORDER_COLOR,
	parseRowGap,
	type TableBorder,
} from "./tableSanitizer";

/** Fallback when a table carries no border information at all. */
const DEFAULT_BORDER: TableBorder = { color: DEFAULT_BORDER_COLOR, width: 1 };

/**
 * Finds the table node the cursor sits in, falling back to the table matching
 * `targetTableId`. Returns null when the selection is not inside a table.
 *
 * Resolving from the selection matters for pasted or legacy tables: those have
 * no `data-table-id` until the normaliser has run, and "first table in the
 * document" is the wrong table as soon as a page holds more than one.
 */
function findTargetTable(
	editor: Editor,
	targetTableId: string | undefined,
): { node: PMNode; pos: number } | null {
	const { $from } = editor.state.selection;
	for (let depth = $from.depth; depth > 0; depth--) {
		const node = $from.node(depth);
		if (node.type.name === "table") {
			return { node, pos: $from.before(depth) };
		}
	}

	let found: { node: PMNode; pos: number } | null = null;
	editor.state.doc.descendants((node, pos) => {
		if (found) return false;
		if (node.type.name !== "table") return true;
		if (!targetTableId || node.attrs["data-table-id"] === targetTableId) {
			found = { node, pos };
		}
		return false;
	});
	return found;
}

/**
 * The border a newly created cell should draw.
 *
 * `data-border-color` / `data-border-width` on the table are the settings panel's
 * source of truth, so they are read first; an existing cell is the fallback for
 * tables that predate those attributes.
 */
function readBorderFromTable(editor: Editor, targetTableId: string | undefined): TableBorder {
	const target = findTargetTable(editor, targetTableId);
	if (!target) return { ...DEFAULT_BORDER };

	const attrColor = target.node.attrs.borderColor as string | null;
	const attrWidth = target.node.attrs.borderWidth as number | null;
	if (attrColor && typeof attrWidth === "number" && !isNaN(attrWidth)) {
		return { color: attrColor, width: attrWidth };
	}

	let color = attrColor || DEFAULT_BORDER.color;
	let width = typeof attrWidth === "number" && !isNaN(attrWidth) ? attrWidth : DEFAULT_BORDER.width;
	let found = false;

	target.node.descendants((node) => {
		if (found) return false;
		if (node.type.name !== "tableCell" && node.type.name !== "tableHeader") return true;

		const cellColor = node.attrs.borderColor as string | null;
		if (cellColor && !attrColor) color = cellColor;

		// Right border (index 1 in "top right bottom left") is always the full width
		const bw = node.attrs.borderWidth;
		if (typeof bw === "string" && bw) {
			const parts = bw.split(" ");
			const right = parseInt(parts[1] ?? parts[0] ?? "");
			if (!isNaN(right) && right > 0) width = right;
		} else if (typeof bw === "number" && bw > 0) {
			width = bw;
		}

		found = true;
		return false;
	});

	return { color, width };
}

/** Cell coordinates inside a table node, as absolute document positions. */
function collectCells(tableNode: PMNode, tablePos: number): Array<Array<{ pos: number; node: PMNode }>> {
	const rows: Array<Array<{ pos: number; node: PMNode }>> = [];
	tableNode.forEach((rowNode, rowOffset) => {
		if (rowNode.type.name !== "tableRow") return;
		const cells: Array<{ pos: number; node: PMNode }> = [];
		rowNode.forEach((cellNode, cellOffset) => {
			if (cellNode.type.name !== "tableCell" && cellNode.type.name !== "tableHeader") return;
			// +1 for the table's own opening token, +1 for the row's
			cells.push({ pos: tablePos + 1 + rowOffset + 1 + cellOffset, node: cellNode });
		});
		rows.push(cells);
	});
	return rows;
}

/** Merge `border-style: solid` into an existing inline style string. */
function withSolidBorderStyle(style: string | null | undefined): string {
	const cleaned = (style || "")
		.split(";")
		.map((s) => s.trim())
		.filter((s) => s && !s.toLowerCase().startsWith("border-style"))
		.join("; ");
	return cleaned ? `${cleaned}; border-style: solid` : "border-style: solid";
}

/**
 * After addRow / addColumn commands, TipTap creates cells with no border attrs, so the
 * new row or column renders as blank space. This restyles every cell of the
 * table so the new ones match — and so the collapse-avoidance widths (no top
 * border except on the first row, no left border except on the first column)
 * stay correct after the insert shifted everything.
 *
 * Cells carrying their own cell-level styling are left alone; their borders
 * come from the generated per-cell CSS.
 */
export function applyTableBorderToCells(editor: Editor, targetTableId?: string) {
	const target = findTargetTable(editor, targetTableId);
	if (!target) return;

	const { color, width } = readBorderFromTable(editor, targetTableId);
	const rowGap = parseRowGap(target.node.attrs["data-width-xs"] as string | null);

	const { state, view } = editor;
	const pmTr = state.tr;
	let modified = false;

	collectCells(target.node, target.pos).forEach((cells, rowIndex) => {
		cells.forEach(({ pos, node }, colIndex) => {
			const borderWidthValue = cellBorderWidthValue(width, rowIndex === 0, colIndex === 0, rowGap > 0);
			const columnIndex = String(colIndex);
			const ownsStyling =
				node.attrs["data-cell-class"] || node.attrs["data-cell-settings"] || node.attrs["data-cell-id"];

			const nextAttrs: Record<string, unknown> = {
				...node.attrs,
				"data-column-index": columnIndex,
			};

			if (!ownsStyling) {
				nextAttrs.borderColor = color;
				nextAttrs.borderWidth = borderWidthValue;
				nextAttrs.style = withSolidBorderStyle(node.attrs.style as string | null);
			}

			const unchanged =
				node.attrs["data-column-index"] === columnIndex &&
				(ownsStyling ||
					(node.attrs.borderColor === color &&
						node.attrs.borderWidth === borderWidthValue &&
						node.attrs.style === nextAttrs.style));
			if (unchanged) return;

			pmTr.setNodeMarkup(pos, undefined, nextAttrs);
			modified = true;
		});
	});

	if (modified) view.dispatch(pmTr);
}

/**
 * Applies a cell alignment attr to all currently selected table cells.
 *
 * Three things have to line up for the change to survive:
 * 1. the TipTap node attr, so renderHTML emits the inline CSS immediately;
 * 2. the cell's `data-cell-settings` JSON, which the CSS generator turns into
 *    `!important` rules that would otherwise reinstate the old value;
 * 3. the `cell-style-<hash>` class, which is derived from those settings.
 *
 * (3) is the part that used to be missing. Cells styled together share one
 * hash class, and the generator emits that class's CSS from whichever cell it
 * meets first — so realigning any other cell in the group had no visible
 * effect. Rehashing gives a diverging cell its own class.
 *
 * Uses onMouseDown + preventDefault on buttons to prevent editor focus-loss
 * before this handler fires, keeping the ProseMirror selection intact.
 */
export function applyAlignmentToSelectedCells(
	editor: Editor,
	attrName: "textAlign" | "verticalAlign",
	value: string,
) {
	const { state, view } = editor;
	const { selection } = state;
	const pmTr = state.tr;
	let modified = false;

	// compact JSON key used by the CSS generator (ta = textAlign, va = verticalAlign)
	const compactKey = attrName === "textAlign" ? "ta" : "va";

	const selectedPositions: number[] = [];

	// CellSelection from prosemirror-tables exposes forEachCell
	if (typeof (selection as unknown as Record<string, unknown>).forEachCell === "function") {
		(selection as unknown as { forEachCell: (fn: (node: PMNode, pos: number) => void) => void })
			.forEachCell((_node, pos) => selectedPositions.push(pos));
	} else {
		// Regular cursor – find the containing cell
		const { $from } = selection;
		for (let d = $from.depth; d > 0; d--) {
			const node = $from.node(d);
			if (node.type.name === "tableCell" || node.type.name === "tableHeader") {
				selectedPositions.push($from.before(d));
				break;
			}
		}
	}

	for (const cellPos of selectedPositions) {
		const node = state.doc.nodeAt(cellPos);
		if (!node) continue;

		// Start from whatever the cell already stores, so alignment does not wipe
		// its padding, colours or borders.
		let styles: Record<string, Record<string, unknown>> = {};
		const rawSettings = node.attrs["data-cell-settings"] as string | null;
		if (rawSettings) {
			try {
				const parsed = JSON.parse(rawSettings) as Record<string, unknown>;
				styles = (parsed.s ?? parsed.styles ?? {}) as Record<string, Record<string, unknown>>;
			} catch {
				styles = {};
			}
		}
		if (Object.keys(styles).length === 0) styles = { xs: {} };

		for (const bp of Object.keys(styles)) {
			if (styles[bp] && typeof styles[bp] === "object") styles[bp][compactKey] = value;
		}

		const settingsJson = JSON.stringify({ s: styles });
		const cellClass = `cell-style-${hashCode(JSON.stringify(styles))}`;
		const classList = ((node.attrs.class as string | null) || "")
			.split(/\s+/)
			.filter((c) => c && !c.startsWith("cell-style-"));
		classList.push(cellClass);

		pmTr.setNodeMarkup(cellPos, undefined, {
			...node.attrs,
			[attrName]: value,
			class: classList.join(" "),
			"data-cell-class": cellClass,
			"data-cell-settings": settingsJson,
		});
		modified = true;
	}

	if (modified) view.dispatch(pmTr);
}
